/**
 * mobileDownload.js — iOS-safe blob download helper.
 *
 * User report (2026-02-28): "when I do it on my iPhone…once you hit
 * download it takes you to a separate window and then when you go
 * back, everything is lost."
 *
 * Root cause: `<a download href="blob:…">` on iOS Safari is treated as
 * a navigation, not a download.  The blob URL replaces the current tab
 * (or opens a new one) and iOS aggressively evicts the parent React
 * SPA from memory when the user returns → React re-mounts fresh and
 * every wizard state variable is gone.
 *
 * Fix:
 *   1) On iOS, try the Web Share API (`navigator.share({files: [file]})`).
 *      This surfaces the native "Save to Files / Print" sheet WITHOUT
 *      navigating the current tab, so the SPA never gets evicted.
 *   2) If Share API isn't available (older iOS), fall back to opening
 *      the blob URL in a new tab via `window.open(_, '_blank')`.  This
 *      still risks state loss on old devices — but the wizard's
 *      sessionStorage persistence layer restores everything on
 *      re-mount, so worst case the user is right back where they
 *      started.
 *   3) On desktop, use the classic `<a download>` click.
 */

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPad on iPadOS 13+ reports "Mac" but has touch — treat as iOS too.
  const isTouchMac = ua.includes('Macintosh') && (navigator.maxTouchPoints || 0) > 1;
  return /iPad|iPhone|iPod/.test(ua) || isTouchMac;
}

/**
 * Download or share a blob in the most user-friendly way for the
 * current device.  Returns a Promise that resolves when the download
 * has been triggered (or the share sheet dismissed).
 */
export async function downloadBlobMobileSafe(blob, filename, opts = {}) {
  const { shareTitle = filename, shareText = '' } = opts;

  // ── Path A: iOS + Web Share API with file support ────────────────
  if (isIOS() && typeof navigator !== 'undefined' && navigator.share) {
    try {
      const file = new File([blob], filename, { type: blob.type || 'application/pdf' });
      // canShare with files was added in iOS 15.  Fall back if missing.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: shareTitle, text: shareText });
        return { via: 'share' };
      }
    } catch (err) {
      // User can cancel share sheet — that throws AbortError.  Silent.
      if (err?.name === 'AbortError') return { via: 'share', cancelled: true };
      // Any other error → fall through to blob-url path.
    }
  }

  // ── Path B: iOS without Share support → open blob url in-tab ─────
  // Using window.location = url keeps the SPA on the same tab so
  // React state isn't wiped.  Safari renders PDFs inline; user taps
  // Share → Save to Files.  Then the browser's Back button returns
  // to the SPA with state intact.
  if (isIOS()) {
    const url = URL.createObjectURL(blob);
    // Anchor with target=_blank + rel=noopener nudges Safari to open
    // a new tab (which it does inline) rather than replacing the SPA.
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Delay revoke so Safari has time to fetch the blob.
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }, 30000);
    return { via: 'ios-tab' };
  }

  // ── Path C: Desktop / Android → classic <a download> ─────────────
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { via: 'anchor' };
}

export { isIOS };
